/**
 * @file: ChatCardShell.tsx
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 *
 * Shared surface for the chat business cards: a fixed-width rounded card
 * with elevation (no border) + an optional left status accent bar (the same
 * `StatusSideBar` the task card uses). The shadow lives on the OUTER view so
 * the inner `overflow: hidden` — needed to clip the accent bar to the rounded
 * corners — doesn't swallow it on iOS. Children own their padding.
 */

import { StatusSideBar } from '@euler/app/components/task-card/components/StatusSideBar';
import { StatusPaletteKey } from '@euler/app/components/task-card/types';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { FC, ReactNode } from 'react';
import { TouchableOpacity, View } from 'react-native';

export const ChatCardShell: FC<{
  onPress?: () => void;
  /** Status palette → left accent bar (mirrors the task card). */
  accent?: StatusPaletteKey;
  /** Card width; defaults to a compact bubble width. */
  width?: number;
  children: ReactNode;
}> = ({ onPress, accent, width, children }) => {
  const { rpx } = useResponsiveContext();
  const radius = rpx(10);
  return (
    <TouchableOpacity
      activeOpacity={0.85}
      onPress={onPress}
      style={{
        width: width ?? rpx(248),
        borderRadius: radius,
        backgroundColor: '#fff',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: rpx(1) },
        shadowOpacity: 0.1,
        shadowRadius: rpx(4),
        elevation: 2,
      }}
    >
      <View
        style={{
          borderRadius: radius,
          overflow: 'hidden',
          backgroundColor: '#fff',
        }}
      >
        {accent ? <StatusSideBar palette={accent} /> : null}
        {children}
      </View>
    </TouchableOpacity>
  );
};
